import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { searchPpwrFeesRequest } from 'redux/reducers/ppwr/reducer';
import { getPpwrFees, getPpwrFeesLoading } from 'redux/reducers/ppwr/selectors';
import { TPpwrFee } from 'redux/reducers/ppwr/types';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { EprServiceFeeDialog } from './EprServiceFeeDialog';

const EDIT_ACTION = 'Edit';

export const EprServiceFees: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [feeToEdit, setFeeToEdit] = useState<TPpwrFee | null>(null);

	const list = useSelector(getPpwrFees);
	const listLoading = useSelector(getPpwrFeesLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPpwrFee>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchPpwrFeesRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAddNew = useCallback(() => {
		setFeeToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setFeeToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setFeeToEdit(null);
	}, []);

	return (
		<>
			<EprServiceFeeDialog open={dialogOpen} onClose={handleDialogClose} feeToEdit={feeToEdit} />
			<EasTableWithFilter<TPpwrFee>
				tableStructure={currentTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / PPWR / <b>EPR Service Fees</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New EPR Service Fee
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
